import axios from "axios";
import React, { useEffect, useState } from "react";
import connectCrewLogo from "../../assets/img/connectid-rp-logo.png";
import { connectIDConfig } from "../../config/connectid";
import ConnectIDService, { Participant } from "../../services/ConnectIDService";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../store";
import { setShowNotVerifiedErrorAction } from "../../store/auth/slice";
import ErrorMessage from "../UI/Tooltips/ErrorMessage";
import "./ConnectIDButton.scss";

// Add TypeScript interface for the global window object
declare global {
  interface Window {
    cidInitIdpSelector: (options: {
      onProceed: (bank: any) => void;
      buttonLabel:
        | "Verify"
        | "Sign Up"
        | "Age check"
        | "Authenticate"
        | "Sign up"
        | "Checkout"
        | "Login"
        | "Verify with ConnectID";
      rpLogoUrl: string;
      certificationStatus: string;
      claims: string[];
      participantsUri: string;
      enableManualVerification: boolean;
      theme: "light" | "dark";
      baseUrl: string;
      redirectUri: string;
      origin: string;
      rpDataSharingDescription: string;
    }) => void;
  }
}

// Valid button labels according to ConnectID requirements
type ValidButtonLabel =
  | "Verify"
  | "Sign Up"
  | "Age check"
  | "Authenticate"
  | "Sign up"
  | "Checkout"
  | "Login"
  | "Verify with ConnectID";

interface ConnectIDButtonProps {
  onSuccess?: (userData: any) => void;
  onLoad?: (userData?: any) => void;
  claims?: string[];
  buttonLabel?: ValidButtonLabel;
  theme?: "light" | "dark";
}

const ConnectIDButton: React.FC<ConnectIDButtonProps> = ({
  onSuccess,
  onLoad,
  claims = Object.keys(connectIDConfig.defaultClaims),
  buttonLabel = "Verify",
  theme = "light",
}) => {
  const dispatch = useDispatch();

  const showNotVerifiedError = useSelector((state) => state.auth.showNotVerifiedError)

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [showSelector, setShowSelector] = useState(true);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const isConnectIdVerified = useSelector(
    (state) => state.onboarding.isConnectIdVerified
  );

  // Validate button label to ensure it's one of the allowed values
  const validatedButtonLabel: ValidButtonLabel = [
    "Verify",
    "Sign Up",
    "Age check",
    "Authenticate",
    "Sign up",
    "Checkout",
    "Login",
  ].includes(buttonLabel)
    ? (buttonLabel as ValidButtonLabel)
    : "Verify";

  // Handle callback from ConnectID
  useEffect(() => {
    // Check if we're in a callback scenario (after bank authorization)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const iss = urlParams.get("iss");

    if (code && state && iss) {
      handleCallback(code, state, iss);
    }
  }, []);

  // Load script and initialize ConnectID
  useEffect(() => {
    if (isConnectIdVerified !== true) {
      // Skip if already initialized
      if (isInitialized) {
        return;
      }

      // Function to load the ConnectID script
      const loadConnectIDScript = () => {
        return new Promise<void>((resolve, reject) => {
          const existingScript = document.getElementById(
            "cid-idp-selector-script"
          );
          if (existingScript && typeof window.cidInitIdpSelector !== 'undefined') {
            setScriptLoaded(true);
            resolve();
            return;
          }

          // console.log("Loading ConnectID script...");
          const script = document.createElement("script");
          script.id = "cid-idp-selector-script";
          script.src =
            "https://cdn.connectid.com.au/idp-selector/9-latest/cid-idp-selector.js";
          script.async = true;

          script.onload = () => {
            // Wait for window.cidInitIdpSelector to be available
            const checkGlobalFunction = setInterval(() => {
              if (typeof window.cidInitIdpSelector !== 'undefined') {
                clearInterval(checkGlobalFunction);
                setScriptLoaded(true);
                if (onLoad) {
                  onLoad(undefined);
                }
                resolve();
              }
            }, 50); // Check every 50ms
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkGlobalFunction);
              if (typeof window.cidInitIdpSelector === 'undefined') {
                console.error("ConnectID function not available after script load");
                setError("Failed to initialize ConnectID script");
                reject(new Error("ConnectID function not available"));
              }
            }, 5000);
          };

          script.onerror = (error) => {
            console.error("Error loading ConnectID script:", error);
            setError("Failed to load ConnectID script");
            reject(error);
          };

          document.body.appendChild(script);
        });
      };

      // Function to initialize the ConnectID selector
      const initializeSelector = async () => {
        if (!scriptLoaded || isInitialized) {
          return;
        }

        try {
          setIsLoading(true);
          setError(null);

          // Fetch participants if not already loaded
          // if (participants.length === 0) {
          //   const fetchedParticipants =
          //     await ConnectIDService.getParticipants();
          //   setParticipants(fetchedParticipants);
          // }

          // Initialize the selector
          if (typeof window.cidInitIdpSelector !== 'undefined') {
            const options = {
              onProceed: handleBankSelection,
              buttonLabel: validatedButtonLabel,
              rpLogoUrl: connectCrewLogo,
              certificationStatus: "Active",
              claims,
              participantsUri: connectIDConfig.participantsUrl,
              enableManualVerification: true,
              theme,
              baseUrl: connectIDConfig.baseUrl,
              redirectUri: connectIDConfig.redirectUri,
              origin: connectIDConfig.origin,
              rpDataSharingDescription: "Your details will be shared for the purpose of verifying your identity with Connected Crew",
              introTextType: 'verify-trust',
              logoLockupStyle: 'dark',
              showInfoContent: true,
              type: 'full process'
            };

            // Make sure we have a small delay to ensure the DOM is ready
            setTimeout(() => {
              const selectorElement =
                document.getElementById("cid-idp-selector");
              if (selectorElement) {
                window.cidInitIdpSelector(options);
                setIsInitialized(true);
              } else {
                console.error(
                  "Could not find cid-idp-selector element in the DOM"
                );
                setError(
                  "Could not initialize ConnectID selector. Please try again."
                );
                setShowSelector(false);
              }
            }, 100);
          } else {
            throw new Error("ConnectID selector script not loaded properly");
          }
        } catch (err) {
          console.error("Error initializing ConnectID:", err);
          if (axios.isAxiosError(err)) {
            console.error("Axios error details:", {
              status: err.response?.status,
              statusText: err.response?.statusText,
              data: err.response?.data,
            });

            if(err.response?.data.error !== "") {
              setError(err.response?.data.error)
            } else {
              setError(err instanceof Error ? err.message : String(err));
            }
          } else {
            setError(err instanceof Error ? err.message : String(err));
          }
          setShowSelector(false);
        } finally {
          setIsLoading(false);
        }
      };

      // Main execution flow
      const setupConnectID = async () => {
        try {
          // First load the script
          await loadConnectIDScript();
          // Then initialize the selector
          await initializeSelector();
        } catch (error) {
          console.error("Error setting up ConnectID:", error);
        }
      };

      setupConnectID();
    }
  }, [scriptLoaded, isInitialized, isConnectIdVerified]);

  const handleCallback = async (code: string, state: string, iss: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const userData = await ConnectIDService.retrieveUserDetails(
        code,
        state,
        iss
      );

      // Clear the URL parameters without refreshing the page
      window.history.replaceState({}, document.title, window.location.pathname);

      if (onSuccess) {
        onSuccess(userData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBankSelection = async (bank: any) => {
    try {
      dispatch(setShowNotVerifiedErrorAction(false))
      setIsLoading(true);
      setError(null);

      // Extract the authorisationServerId from the bank object
      const authorisationServerId =
        typeof bank === "string" ? bank : bank?.authorisationServerId;

      if (!authorisationServerId) {
        throw new Error(
          "Could not extract authorisationServerId from bank selection"
        );
      }

      // Call the API to select the bank
      const response = await ConnectIDService.selectBank(
        authorisationServerId,
        claims
      );

      // Redirect to the authorization URL
      window.location.href = response.authUrl;
    } catch (err) {
      console.error("Error selecting bank:", err);
      if (axios.isAxiosError(err)) {
        console.error("Axios error details:", {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
        });
      }
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="d-flex flex-column gap-20">
      <div
        className={`connectid-button-container position-relative ${
          isLoading ? "loading-class" : ""
        }`}
      >
        <div id="cid-idp-selector"></div>
        {error && <ErrorMessage message={error} />}
        {showNotVerifiedError && <ErrorMessage message={"Verification wasn’t completed, Please try again"} />}
      </div>

      {isLoading && (
        <div className="connectid-loading d-flex gap-10 align-items-center text-black fs-16">
          <div className="connectid-spinner" />
          Loading ConnectID verification...
        </div>
      )}
    </div>
  );
};

export default ConnectIDButton;
